import { Link } from "react-router";
import { cn } from "@pracownik/ui";
import { brand } from "@/lib/brand";

interface BrandLogoProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  to?: string;
  /** Hide the wordmark and show only the mark (compact headers). */
  markOnly?: boolean;
}

const SIZE = {
  sm: { mark: "size-6 rounded-md", text: "text-base" },
  md: { mark: "size-7 rounded-lg", text: "text-lg" },
  lg: { mark: "size-9 rounded-xl", text: "text-2xl" },
};

/** Brand mark: an envelope-shaped tile with a spark — a worker who answers mail. */
export function BrandMark({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center bg-primary text-primary-foreground shadow-[inset_0_1px_0_0_oklch(1_0_0/0.18)]",
        className,
      )}
    >
      <svg
        viewBox="0 0 24 24"
        className="size-[62%]"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 7.5 12 13l8-5.5" />
        <path d="M4 7.5V17a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V7.5" />
        <path d="M4 7.5A1.5 1.5 0 0 1 5.5 6h13A1.5 1.5 0 0 1 20 7.5" />
      </svg>
    </span>
  );
}

export function BrandLogo({ className, size = "md", to = "/", markOnly = false }: BrandLogoProps) {
  const s = SIZE[size];
  const [head, tail] = brand.wordmark;
  return (
    <Link
      to={to}
      className={cn(
        "inline-flex items-center gap-2.5 rounded-lg outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40",
        className,
      )}
      aria-label={brand.name}
    >
      <BrandMark className={s.mark} />
      {!markOnly && (
        <span className={cn("font-display font-semibold tracking-tight leading-none", s.text)}>
          {head}
          <span className="text-text-tertiary">{tail}</span>
        </span>
      )}
    </Link>
  );
}
